import { useContext, useEffect, useState } from "react";
import { useAuth } from "@humansignal/core/providers/AuthProvider";
import { useAPI } from "@humansignal/core";
import { SidebarMenu } from "../../components/SidebarMenu/SidebarMenu";
import { ProjectContext } from "../../providers/ProjectProvider";
import { useOnlineStatus } from "../../hooks/useOnlineStatus";
import { WebhookPage } from "../WebhookPage/WebhookPage";
import { DangerZone } from "./DangerZone";
import { GeneralSettings } from "./GeneralSettings";
import { VisibilitySettings } from "./VisibilitySettings";
import { AnnotationSettings } from "./AnnotationSettings";
import { LabelingSettings } from "./LabelingSettings";
import { QualitySettings } from "./QualitySettings";
import { ReviewSettings } from "./ReviewSettings";
import { SecuritySettings } from "./SecuritySettings";
import { AudioQCSettings, projectHasAudioRecord } from "./AudioQCSettings";
import { ContributionsSettings } from "./ContributionsSettings";
import { TranslationSettings, projectHasTextArea } from "./TranslationSettings";
import { ConsentSettings } from "./ConsentSettings";
import { LicensingSettings } from "./LicensingSettings";
import { HuggingFaceHubSettings } from "./HuggingFaceHubSettings";
import { TypedLayersSettings } from "./TypedLayersSettings";
import { MachineLearningSettings } from "./MachineLearningSettings/MachineLearningSettings";
import { SettingsSearch } from "./SettingsSearch";
import { PredictionsSettings } from "./PredictionsSettings/PredictionsSettings";
import { StorageSettings } from "./StorageSettings/StorageSettings";
import { ProjectNav } from "../ProjectDashboard/ProjectNav";
import { settingsTabs as extensionSettingsTabs } from "@afriannotate/extensions";
import "./settings.prefix.css";

// Top-nav pill row for project-scoped pages (Dashboard / Members /
// Data / Settings). Mounted as SettingsPage.context so the same tabs
// render on every project sub-page. Managers always see the "Members"
// + "Settings" tabs since this page is itself gated to managers.
const SettingsPageContext = () => {
  const { project } = useContext(ProjectContext);
  if (!project?.id) return null;
  // Settings is manager-only, so user role / project flags don't gate
  // the Data Manager pill here — just pass them through for
  // consistency / future reuse.
  return <ProjectNav projectId={project.id} canManage={true} project={project} />;
};

export const MenuLayout = ({ children, ...routeProps }) => {
  const { user } = useAuth();
  const { project } = useContext(ProjectContext);
  const api = useAPI();
  // Access control is handled centrally by ``RouteGuard`` — if we
  // got here, the policy already approved the user. We still fetch
  // project-specific permissions because sub-pages use them to
  // decide which controls to disable / show (e.g. read-only vs
  // editable). No redirect logic here anymore.
  const [perms, setPerms] = useState(null);
  const online = useOnlineStatus();

  useEffect(() => {
    if (!project?.id) return;
    api.callApi("projectMyPermissions", { params: { pk: project.id } }).then((res) => {
      if (res && !res.error) setPerms(res);
    });
  }, [api, project?.id]);

  // Settings pages from an installed extension: a page may name the menu
  // section it belongs to (``menuSection``) and decide whether it applies
  // to the project (``appliesTo``).
  const extensionPages = (section) =>
    Object.values(extensionSettingsTabs?.project ?? {}).filter(
      (page) => page.menuSection === section && (page.appliesTo?.(project) ?? true),
    );
  const hiddenSettings = project?.extensions?.hidden_settings ?? [];

  if (!perms) return null; // wait once for the perms payload so sub-pages can read it

  return (
    <SidebarMenu
      search={<SettingsSearch base={routeProps.match.url} />}
      menuItems={[
        // Logical sections so the (long) settings list reads as coherent
        // clusters instead of a flat scatter. Section markers are
        // ``{ header }`` objects (see Menu.Builder); every group below has
        // at least one always-present page so no header is ever orphaned.
        { header: "General" },
        GeneralSettings,
        VisibilitySettings, // who can see + contribute (crowdsource)
        SecuritySettings, // who may take the data off the site, the local mirror
        ...extensionPages("general"),

        { header: "Labeling" },
        LabelingSettings,
        AnnotationSettings,
        // Pages an installed extension adds to this section.
        ...extensionPages("labeling"),
        // Typed feature layers — typed metadata schema on spans/relations/docs.
        TypedLayersSettings,
        // Translation tools (TM + glossary) — only for TextArea projects.
        projectHasTextArea(project) ? TranslationSettings : null,

        { header: "Review & quality" },
        ReviewSettings,
        QualitySettings,
        // Audio QC — a quality check on projects that record (<AudioRecord> or <VideoRecord>).
        projectHasAudioRecord(project) ? AudioQCSettings : null,
        ...extensionPages("review"),

        // The rules about the data itself: consent, licence, level, pay.
        { header: "Governance" },
        ConsentSettings,
        LicensingSettings,
        ...extensionPages("governance"),
        ContributionsSettings, // validated-work ledger + rate card + payouts

        // Where the data lives.
        { header: "Data" },
        StorageSettings,
        HuggingFaceHubSettings, // bidirectional Hub connector
        ...extensionPages("data"),

        // What predicts.
        { header: "Models" },
        MachineLearningSettings,
        PredictionsSettings,
        ...extensionPages("models"),

        // What leaves.
        { header: "Sharing" },
        WebhookPage,
        ...extensionPages("sharing"),

        ...extensionPages(undefined),

        "DIVIDER",
        DangerZone,
      ]
        .filter(Boolean)
        // Pages an extension says do not apply to this project
        // (``project.extensions.hidden_settings``, by path).
        .filter((item) => !item?.path || !hiddenSettings.includes(item.path))}
      path={routeProps.match.url}
    >
      {/*
        When offline, every settings sub-page goes read-only.
        ``<fieldset disabled>`` is a one-liner DOM primitive that
        cascades a disabled state into every nested input / select /
        textarea / button (including submit buttons) — no per-page
        plumbing needed. The banner above tells the user what
        happened so they don't think the form is broken.
        Settings can't safely save while upstream is dead anyway:
        the writes touch shared state (org policy, member roles,
        export controls) and queueing them locally would let one
        annotator's device hold a "this project's edit policy"
        change for hours before other users see it. So: read-only
        offline, save when network returns.
      */}
      {!online && (
        <div
          role="status"
          aria-live="polite"
          style={{
            padding: "10px 14px",
            margin: "0 0 16px",
            borderRadius: 8,
            background: "var(--color-warning-background, #fef6e7)",
            color: "var(--color-warning-content, #7a4f00)",
            border: "1px solid var(--color-warning-border, rgba(195, 142, 0, 0.3))",
            fontSize: "var(--font-size-caption, 13px)",
            lineHeight: 1.45,
          }}
        >
          You're offline. Settings are read-only, your edits will save once you're back online.
        </div>
      )}
      <fieldset disabled={!online} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
        {children}
      </fieldset>
    </SidebarMenu>
  );
};

// Review Queue moved into the Data Manager's Analytics modal
// (``ProjectMetricsModal``) as its first tab. Kept out of this
// dict so the legacy ``/settings/review`` route 404s — the queue
// UI is reachable via the DM toolbar's Analytics button.
const rawPages = {
  VisibilitySettings,
  AnnotationSettings,
  LabelingSettings,
  QualitySettings,
  TypedLayersSettings,
  TranslationSettings,
  ReviewSettings,
  AudioQCSettings,
  ContributionsSettings,
  ConsentSettings,
  LicensingSettings,
  HuggingFaceHubSettings,
  SecuritySettings,
  MachineLearningSettings,
  PredictionsSettings,
  StorageSettings,
  WebhookPage,
  // Project settings pages contributed by an installed extension package;
  // each value is a page component carrying ``title`` and ``path``.
  ...(extensionSettingsTabs?.project ?? {}),
  DangerZone,
};

// Attach ``SettingsPageContext`` to every Settings sub-page that
// doesn't already carry one, so the right-side ProjectNav pill row
// renders on ``/settings/consent`` / ``/settings/quality`` etc. and
// not only on the base ``/settings`` route (which registers context
// on the SettingsPage config object directly).
//
// IMPORTANT: many of these pages are exported as React function
// components (``export const ConsentSettings = () => …``). Spreading
// a function via ``{ ...page }`` returns only its static properties
// and silently loses the function reference, which then blows up
// downstream in ``routeHelpers::pageSetToRoutes`` at
// ``route.modal = !!page.modal`` because the spread object has no
// ``.component`` field either. So we assign ``.context`` DIRECTLY
// on the function (which JS lets you do — functions are objects)
// when the entry is a component, and only spread when the entry is
// already a config object.
const pages = Object.fromEntries(
  Object.entries(rawPages).map(([key, page]) => {
    if (!page) return [key, page];
    if (typeof page === "function") {
      if (page.context == null) page.context = SettingsPageContext;
      return [key, page];
    }
    if (page.context != null) return [key, page];
    return [key, { ...page, context: SettingsPageContext }];
  }),
);

export const SettingsPage = {
  title: "Settings",
  path: "/settings",
  exact: true,
  layout: MenuLayout,
  component: GeneralSettings,
  context: SettingsPageContext,
  pages,
};
